/**
 * Quadratic easing function for smooth animations.
 * @param {number} t - The specified time when the animation will start.
 * @param {number} b - The specified starting position of the object on the x-axis.
 * @param {number} c - The specified change in value for the object.
 * @param {number} d - The specified duration of the whole process.
 *
 * The function divides the animation into two parts:
 * 1. An "easing in" part (when t < 1), where the pace of change starts slowly and then accelerates.
 * 2. An "easing out" part (when t >= 1), where the pace of change starts quickly and then decelerates.
 *
 * @returns {number} The new position of the object.
 */
const easeInOutQuad = (t, b, c, d) => {
	t /= d / 2
	if (t < 1) return (c / 2) * t * t + b
	t--
	return (-c / 2) * (t * (t - 2) - 1) + b
}

/**
 * Smoothly scrolls an element to a target position over a specified duration.
 * @param {Element} element - The DOM element to be scrolled.
 * @param {number} target - The target scroll position.
 * @param {number} duration - The duration of the scroll animation in milliseconds.
 * @param {number} lastKnownPosition - an optional value representing the last recorded scroll position of parent component
 *
 * @returns {void}
 */
export const smoothScrollTo = (element, target, duration, lastKnownPosition) => {
	const start = lastKnownPosition ? lastKnownPosition : element.scrollTop
	const change = target - start
	const startTime = performance.now()
	/**
	 * Calculates the new scroll position of the element at the time of the next DOM repaint, and updates the element's scrollTop property.
	 * If the animation duration has not been reached, it requests the next animation frame.
	 *
	 * @param { DOMHighResTimeStamp } currentTime - the timestamp of the frame executing this function
	 * @returns {void}
	 */
	function animateScroll(currentTime) {
		const elapsedTime = currentTime - startTime
		const nextScrollTop = easeInOutQuad(Math.min(elapsedTime, duration), start, change, duration)
		element.scrollTop = nextScrollTop

		if (elapsedTime < duration) { // if we have more time, do more animation
			window.requestAnimationFrame(animateScroll)
		} else { // if we're out of time, jump to the target
			element.scrollTop = target
		}
	}
	// the DOMHighResTimeStamp gets passed in from the params passed into rAF's callback
	window.requestAnimationFrame(animateScroll)
}

/**
 * Creates a synthetic download Anchor element,
 * assigns the href as the url passed into the function,
 * and then synthetically clicks the link.
 *
 * @param {string} url - The URL to be clicked. Usually resolves to a downloadable asset.
 *
 * The intended use for this fn is to more easily download files to client's desktop
 * @returns {void}
 */
export const createAndClickDownloadLink = (url) => {
	const linkUrl = URL.createObjectURL(url)
	const link = document.createElement("a")
	link.setAttribute("download", "")
	link.setAttribute("target", "_blank")
	link.href = linkUrl
	link.click()
	URL.revokeObjectURL(linkUrl)
}

/**
 * Takes a blob fetched from somewhere,
 * transforms it into an Object URL,
 * then creates a synthetic Anchor element and clicks it to initiate download
 *
 * @param blob {Blob} - a Blob object representing the file being downloaded
 * @param fileName {string} - the fileName for the downloaded file.
 * @return {void}
 */
export const createAndClickDownloadLinkFromBlob = (blob, fileName) => {
	const blobAsUrl = URL.createObjectURL(blob)
	const link = document.createElement("a")
	link.setAttribute("download", fileName)
	link.setAttribute("target", "_blank")
	link.href = blobAsUrl
	link.click()
	URL.revokeObjectURL(blobAsUrl)
}